<template>
  <div class="payment-block block">
    <div class="payment-controls">
      <h2>{{ $t('paymentMethods.selectPaymentMethod') }}:  <v-progress-circular :size="20" width="2" color="primary" indeterminate v-if="loading" class="ml-3"/></h2>
      <v-tabs
        v-if="isVisibleGateway && !loading && !isVirtualHostingV2"
        v-model="selectedPersonIndex"
        hide-slider
        height="40"
      >
        <v-tab
          v-for="(type, index) in personTypes"
          :key="type.key"
          :value="index"
          :ripple="false"
          :data-attr="`methods-${type.key}`"
        >
          <v-icon v-if="selectedPersonIndex === index" class="sr-icon-check" />
          {{ $t('paymentMethods.personType.' + type.key) }}
        </v-tab>
      </v-tabs>
    </div>

    <template v-if="showBalance">

      <div v-if="showBalanceInfo" class="unit">
        <p v-html="$t('paymentMethods.balanceCreditInfo', {balance: balance, currencySuffix: currencyInfo.suffix, currencyPrefix: currencyInfo.prefix})"></p>
      </div>

      <div v-else-if="domainType !== $options.DOMAIN_TYPE.ASTRA" class="unit">
        <legend
            class="label"
            v-html="$t('paymentMethods.balanceInfo', {balance: balance, currencySuffix: currencyInfo.suffix, currencyPrefix: currencyInfo.prefix})"
            data-attr="balance"
            :data-attr-balance="`${currencyInfo.prefix}${balance}${currencyInfo.suffix}`"
        ></legend>
        <div data-attr="debited-amount">
          <v-text-field
              ref="input"
              :label="$t('paymentMethods.debitedAmount')"
              v-model="paymentAmount"
              outlined
              :prefix="currencyInfo.code === $options.CURRENCY.USD ? '$' : '₴'"
              placeholder="0.00"
          />
        </div>
      </div>
    </template>

    <v-expand-transition>
      <div class="unit" v-if="isVisibleGateway">
        <v-radio-group v-model="paymentMethods" return-object class="payment-methods">
          <v-radio
              v-for="item in filteredGateways"
              :key="item.name"
              :value="item"
              :data-attr="`payment-${item.name}`"
          >
            <template v-slot:label>
              <div class="label-cell">
                <div class="image-holder">
                  <img :src="item.image_url" :alt="item.name">
                </div>
                <div class="name">
                  <div v-if="item.title">
                    {{ item.title[currencyInfo?.lang?.toLowerCase()] }}<span style="white-space: nowrap;">&#160;<v-tooltip v-if="item.tooltip_text[currencyInfo?.lang?.toLowerCase()]" right max-width="330">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                            tag="span"
                            fab
                            @click.stop="$event.preventDefault()"
                        >
                          <v-icon class="sr-icon-help"/>
                        </v-btn>
                      </template>
                      <span v-html="item.tooltip_text[currencyInfo?.lang?.toLowerCase()]"></span>
                    </v-tooltip>
                  </span>
                  </div>
                </div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from "vuex";
  import { SETTINGS_GETTERS, SETTING_ACTIONS } from "@/store/modules/settings";
  import { CURRENCY, DOMAIN_TYPE, FLOW, URLS } from '@/constants';
  import { getGateways, getGatewaysMonobank } from "@/store/api/settings";
  import * as Sentry from "@sentry/vue";

  export default {
    name: "PaymentMethods",
    CURRENCY,
    DOMAIN_TYPE,
    FLOW,
    props: {
      currencyInfo: { type: [Object, Array], default: () => ({}) },
      flow: { type: String },
      freeDomain: { type: Boolean, default: false },
      domainType: { type: String },
      isVirtualHostingV2: { type: Boolean, default: false },
    },
    data: () => ({
      balance: '0.00',
      paymentAmount: '0.00',
      paymentMethods: {},
      gateways: undefined,
      loading: false,
      personTypes: [
        { key: 'physical', value: 'physical_person' },
        { key: 'legal', value: 'legal_person' },
      ],
      selectedPersonIndex: 0,
    }),
    mounted() {
      if (!this.authorization) { this.authorization = this.setAuth(); };
      this.balance = this.authorization?.auth?.credit;
      if (!this.totalPrice) { this.setTotalPrice(); };
      this.paymentAmount = this.totalPrice;
    },
    computed: {
      ...mapGetters('settings', {
        authorization: SETTINGS_GETTERS.AUTH,
        totalPrice: SETTINGS_GETTERS.TOTAL_PRICE,
      }),
      isVisibleGateway() {
        const visible = Number(this.balance) < Number(this.totalPrice) || (Number(this.paymentAmount) !== Number(this.totalPrice) || this.showBalanceInfo || this.domainType === DOMAIN_TYPE.ASTRA);
        return this.isVirtualHostingV2 || visible;
      },
      showBalance() {
        return !!this.authorization.auth && this.authorization.auth.credit > 0;
      },
      showBalanceInfo() {
        return (this.flow === FLOW.DEFAULT || this.flow === FLOW.DOMAIN) && this.authorization.auth.usd_income < 15 && !this.freeDomain;
      },
      filteredGateways() {
        return this.isVirtualHostingV2 ? this.gateways : this.gateways?.filter(gateway => gateway[this.personTypes[this.selectedPersonIndex].value]);
      },
    },
    methods: {
      ...mapActions('settings', {
        setTotalPrice: SETTING_ACTIONS.SET_TOTAL_PRICE,
      }),
      async getGatewaysDto(cookie) {
        this.loading = true;
        try {
          if(this.isVirtualHostingV2) {
            this.gateways = this.gateways || await getGatewaysMonobank();
          } else {
            this.gateways = await getGateways(cookie);
          }
        } catch (error) {
          Sentry.captureException(error);
          this.$notify('error', `Error ${error?.data?.statusCode}`, error?.data?.message || error, { copy: true });
        } finally {
          this.loading = false;
        }
      },
      eventHandler(val) {
        this.$nextTick(() => {
          this.paymentAmount = val.replace(/([^\d.,])|(\s)/g, '');
          this.paymentAmount = this.paymentAmount.replace(/,/g, '.');
          this.paymentAmount = this.paymentAmount.replace(/\..*/, c => `.${c.replace(/\./g, () => "")}` );
          if(this.paymentAmount.includes('.')) {
            this.paymentAmount = this.paymentAmount.substring(0, this.paymentAmount.lastIndexOf('.')+3);
          }
        },)

        clearTimeout(this.timerId);
        this.timerId = setTimeout(() => {
          const paymentAmount = Number(this.paymentAmount);
          const totalPrice = Number(this.totalPrice);
          const balance = Number(this.balance);

          if(paymentAmount > (totalPrice && balance)) {
            const minValue = Math.min(totalPrice, balance)
            this.paymentAmount = minValue.toString();
          } else if(paymentAmount > totalPrice) {
            this.paymentAmount = totalPrice.toString();
          } else if(paymentAmount > balance) {
            this.paymentAmount = balance.toString();
          }
        }, 1200)
      },
      emitPaymentMethod() {
        const obj = {
          name: !this.isVisibleGateway ? "banktransfer" : this.paymentMethods?.name,
          amount: this.domainType === DOMAIN_TYPE.ASTRA ? 0 : Number(this.paymentAmount),
          offer_url: this.paymentMethods?.offer_url
        };
        this.$emit('paymentMethod', obj);
      }
    },
    watch: {
      'authorization.cookie': {
        handler(val) {
          this.getGatewaysDto(val);
        },
        immediate: true,
        deep: true
      },
      loading: {
        handler(val) {
          this.$emit('loading', val);
        },
        immediate: true,
        deep: true
      },
      paymentAmount(val) {
        this.eventHandler(val);
        this.emitPaymentMethod();
      },
      currencyInfo: {
        handler() {
          this.currencySuffix = this.currencyInfo.suffix ? this.currencyInfo.suffix : '';
          this.currencyPrefix = this.currencyInfo.prefix ? this.currencyInfo.prefix : '';
        },
        deep: true
      },
      totalPrice(val) {
        this.eventHandler(val || '0');
      },
      paymentMethods() {
        this.emitPaymentMethod();
      },
      filteredGateways(newGateways) {
        this.paymentMethods = newGateways.find(item => item.selected) || newGateways[0];
      },
    }
  }
</script>

<style lang="scss">
  .payment-block {
    padding-bottom: 50px;

    .v-text-field .v-label.v-label--active {
      background-color: transparent;
    }

    .payment-controls {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-bottom: 15px;
      column-gap: $row-padding;

      .v-tabs {
        width: auto;
        margin: 0 0 17px 0;

        .v-tab {
          padding: 0 24px;
          text-transform: none;
          font-size: 16px;
          color: $black;
          letter-spacing: 0;
          background: $white;

          &:first-child {
            border-radius: 4px 0 0 4px;
          }

          &:last-child {
            border-radius: 0 4px 4px 0;
          }

          &.v-tab--active {
            border: 1px solid rgba(0, 0, 0, 0.20);
            font-weight: 700;
            cursor: default;

            &:hover::before {
              opacity: 0;
            }
          }

          &:not(.v-tab--active) {
            background: $base-bg-color;
            color: $black;

            &:hover::before {
              opacity: 0.12;
            }
          }

          .v-icon {
            font-size: 14px;
            color: $primary;
            margin-right: 4px;
          }

          @media (max-width: $tablet) {
            padding: 0 8px;
            flex-grow: 1;
          }
        }

        .v-slide-group__prev--disabled {
          display: none !important;
        }

        .v-slide-group__content {
          justify-content: flex-end;

          @media (max-width: $tablet) {
            justify-content: stretch;
          }
        }
      }
    }
  }

  .payment-methods {

    .v-input__slot {
      width: 100%;
    }

    .v-input--radio-group__input {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      flex-direction: row;
      width: 100%;
      margin: -10px;
      width: calc(100% + 20px);
      align-items: stretch;
    }

    .v-radio {
      width: 33.33%;
      flex-direction: column;
      align-items: flex-start;
      padding: 10px;
      position: relative;
      margin-bottom: 0 !important;

      &.v-item--active {

        .label-cell {
          background-color: $blue-active;
          border-color: $blue-active;
        }
      }

      @media (max-width: $phablet) {
        width: 50%;
      }

      @media (max-width: $phone) {
        width: 100%;
      }
    }

    .label-cell {
      text-align: center;
      padding: 19px 3px 10px;
      border: 1px solid rgba($black, .2);
      border-radius: 7px;
      height: 100%;
      width: 100%;
      transition: $transition;

      &:hover {
        box-shadow: 2px 2px 12px rgb(#CAD6DE, 1);
      }
    }

    .v-label {
      width: 100%;
    }

    .v-input--selection-controls__input {
      position: absolute;
      top: 20px;
      left: 19px;
      z-index: 2;
    }

    .name {
      font-size: 15px;
      height: calc(100% - 78px);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    .image-holder {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 72px;
      width: 100%;
      margin-bottom: 6px;

      img {
        display: block;
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
      }
    }
  }
</style>
